import { Component, inject, OnInit } from '@angular/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ContactsService } from '@app/services/contacts.service';
import { UsersService } from '@app/services/users.service';
import { CommunicationPreferencesInterface } from '@app/interfaces/communications-preferences.interface';
import { ContactInterface } from '@app/interfaces/contact.interface';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';

const ALL_SET_PREFERENCES: CommunicationPreferencesInterface = {
    disabledCasesEmail: false,
    disabledCasesSms: false,
    disabledMarketingEmail: false,
    disabledMarketingSms: false,
    disabledSystemMsg: false,
} as const;

const DUMMY_CONTACT = {
    commPreferences: ALL_SET_PREFERENCES,
} as ContactInterface;

@Component({
    selector: 'app-communication-preferences-dialog-component',
    templateUrl: './communication-preferences-dialog.component.html',
    styleUrls: ['./communication-preferences-dialog.component.scss'],
    imports: [MatDialogModule, MatIconModule, ReactiveFormsModule, MatSlideToggleModule, MatButtonModule],
})
export class CommunicationPreferencesDialogComponent implements OnInit {
    public readonly dialogRef = inject(MatDialogRef<CommunicationPreferencesDialogComponent>);
    private readonly userService = inject(UsersService);
    private readonly contactService = inject(ContactsService);
    private readonly formBuilder = inject(FormBuilder);

    public loading: boolean = true;
    public preferencesForm!: FormGroup;
    private contactId: string;

    ngOnInit() {
        this.contactId = this.userService.user.contactId;
        this.preferencesForm = this.createForm();

        // @API contacts.get
        this.contactService.get(this.contactId).then((contact: ContactInterface) => {
            this.updateForm(contact);
            this.loading = false;
        });
    }

    public async saveAndClose() {
        if (this.preferencesForm.dirty) {
            const preferencesData = this.preferencesForm.getRawValue();
            delete preferencesData.disabledSystemMsg;

            for (const key in preferencesData) {
                if (preferencesData.hasOwnProperty(key)) {
                    preferencesData[key] = !preferencesData[key];

                    // If the value is false or the field is disabled, we don't want to send it to the server
                    if (!preferencesData[key] || this.preferencesForm.get(key).disabled) {
                        delete preferencesData[key];
                    }
                }
            }

            // @API contacts.patch
            await this.contactService.patch(this.contactId, { commPreferences: preferencesData });
        }

        this.dialogRef.close();
    }

    private createForm(): FormGroup {
        const preferences = Object.assign({}, ALL_SET_PREFERENCES, DUMMY_CONTACT);

        for (const key in preferences) {
            if (preferences.hasOwnProperty(key)) {
                preferences[key] = !preferences[key];
            }
        }

        return this.formBuilder.group({
            disabledSystemMsg: new FormControl({ value: preferences.disabledSystemMsg, disabled: true }),
            disabledCasesEmail: new FormControl({ value: preferences.disabledCasesEmail, disabled: true }),
            disabledCasesSms: new FormControl(preferences.disabledCasesSms),
            disabledMarketingEmail: new FormControl({ value: preferences.disabledMarketingEmail, disabled: true }),
            disabledMarketingSms: new FormControl(preferences.disabledMarketingSms),
        });
    }

    private updateForm(contact: ContactInterface) {
        const preferences = Object.assign({}, ALL_SET_PREFERENCES, contact.commPreferences);

        for (const key in preferences) {
            if (preferences.hasOwnProperty(key)) {
                preferences[key] = !preferences[key];
            }
        }

        const disabledEmails = !contact.email;

        this.preferencesForm.patchValue({
            disabledCasesEmail: preferences.disabledCasesEmail && !disabledEmails,
            disabledCasesSms: preferences.disabledCasesSms,
            disabledMarketingEmail: preferences.disabledMarketingEmail && !disabledEmails,
            disabledMarketingSms: preferences.disabledMarketingSms,
        });

        if (disabledEmails) {
            this.preferencesForm.get('disabledCasesEmail').disable();
            this.preferencesForm.get('disabledMarketingEmail').disable();
        } else {
            this.preferencesForm.get('disabledCasesEmail').enable();
            this.preferencesForm.get('disabledMarketingEmail').enable();
        }
    }
}
